/**
 * Detalhe da atividade (atleta e equipe): totais, comparação prescrito × realizado, voltas,
 * gráfico, vínculo manual com treino, edição, exclusão e download do original.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../../auth/AuthContext";
import { api, ApiError, download } from "../../api/client";
import type { ActivityDetail, ActivityStream, Comparison } from "../../api/types";
import { ActivityChart } from "../../components/ActivityChart";
import { ActivityMetricsView, RpePanel } from "../../components/ActivityMetricsView";
import { ConversationPanel } from "../../components/ConversationPanel";
import { RouteMap } from "../../components/RouteMap";
import { Badge, Button, EmptyState, ErrorState, FormError, Loading, Modal, PageHead, SelectField, TextArea, TextField, useToast } from "../../components/ui";
import { fileSize, localTime, PACE_RESULT, paceRange, pct, plannedVolume, SPORT_LABEL, TOTALS_STATUS } from "../../lib/activities";
import { formatDuration, formatPace, metersToKm } from "../../lib/format";
import { fmtLong, plannedDistance, plannedDuration, WORKOUT_TYPE_LABEL } from "../../lib/training";
import { useAsync } from "../../lib/useAsync";
import { Link, navigate } from "../../router/router";
import { t, tj } from "../../i18n";

export function ActivityDetailPage({
  orgId,
  activityId,
  back,
  workoutHref,
  canEdit,
  canRate = false,
  self = false,
  canMessage = false,
}: {
  orgId: string;
  activityId: string;
  back: { to: string; label: string };
  workoutHref?: (workoutId: string) => string;
  canEdit: boolean;
  /** O próprio atleta informa o esforço percebido. */
  canRate?: boolean;
  /** Visão do próprio atleta (conversa em /me). */
  self?: boolean;
  /** A equipe pode conversar sobre a atividade. */
  canMessage?: boolean;
}) {
  const toast = useToast();
  const { can } = useAuth();
  const path = `/orgs/${orgId}/activities/${activityId}`;
  const state = useAsync(() => api.get<ActivityDetail>(path), [path]);
  const [a, setA] = useState<ActivityDetail | null>(null);
  const [stream, setStream] = useState<ActivityStream | null>(null);
  const [linkTo, setLinkTo] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => setA(state.data), [state.data]);
  useEffect(() => {
    if (state.data?.has_stream) api.get<ActivityStream>(`${path}/stream`).then(setStream).catch(() => setStream(null));
  }, [state.data, path]);
  useEffect(() => setLinkTo(a?.candidates[0]?.id ?? ""), [a?.candidates]);

  if (state.loading && !a) return <Loading />;
  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (!a) return null;

  async function run(action: string, fn: () => Promise<ActivityDetail>, done: string) {
    setBusy(action);
    try {
      setA(await fn());
      toast(done);
    } catch (e) {
      toast((e as ApiError).message, "error");
    } finally {
      setBusy(null);
    }
  }

  const facts: [string, string][] = [
    [t("Distância"), metersToKm(a.distance_m)],
    [t("Tempo em movimento"), formatDuration(a.moving_time_s)],
    [t("Ritmo médio"), formatPace(a.pace_s_km)],
    [t("FC média"), a.avg_hr ? `${a.avg_hr} bpm` : "—"],
    ...((a.calories
      ? [[t("Calorias"), `${a.calories} kcal`]]
      : a.estimated_kcal
        ? [[t("Calorias estimadas"), `≈ ${a.estimated_kcal} kcal`]]
        : []) as [string, string][]),
    [t("Tempo total"), formatDuration(a.elapsed_time_s)],
  ];

  return (
    <>
      <p style={{ marginBottom: 8 }}>
        <Link to={back.to}>{back.label}</Link>
      </p>
      <PageHead
        title={a.name}
        subtitle={t("{local_date} às {timezone}. {sport}{device}.", { local_date: fmtLong(a.local_date), timezone: localTime(a.start_time, a.timezone), sport: SPORT_LABEL[a.sport], device: a.device ? `, ${a.device}` : "" })}
        actions={
          <>
            {a.file && can("self.activities") && (
              <Button onClick={() => download(`${path}/original`, a.file!.name).catch((e) => toast((e as ApiError).message, "error"))}>
                {t("Baixar original")}
              </Button>
            )}
            {canEdit && <Button onClick={() => setEditing(true)}>{t("Editar")}</Button>}
            {canEdit && (
              <Button variant="danger" onClick={() => setDeleting(true)}>
                {t("Excluir")}
              </Button>
            )}
          </>
        }
      />

      <dl className="scoreboard activity-facts">
        {facts.map(([k, v]) => {
          // a unidade (km, /km, kcal, bpm) fica menor ao lado do número, para o valor caber numa linha só
          const m = /^(.*?)\s*(\/km|km|kcal|bpm)$/.exec(v);
          return (
            <div className="metric" key={k}>
              <dt>{k}</dt>
              <dd>{m ? <>{m[1]}<small>{m[2]}</small></> : v}</dd>
            </div>
          );
        })}
      </dl>
      <dl className="facts section" style={{ marginTop: 16 }}>
        {a.max_hr && (
          <>
            <dt>{t("FC máxima")}</dt>
            <dd className="num">{t("{v} bpm", { v: a.max_hr })}</dd>
          </>
        )}
        {a.avg_cadence_spm && (
          <>
            <dt>{t("Cadência média")}</dt>
            <dd className="num">{t("{v} passos/min", { v: a.avg_cadence_spm })}</dd>
          </>
        )}
        {a.elevation_gain_m !== null && (
          <>
            <dt>{t("Ganho de elevação")}</dt>
            <dd className="num">{a.elevation_gain_m} m</dd>
          </>
        )}
        {a.notes && (
          <>
            <dt>{t("Observações")}</dt>
            <dd>{a.notes}</dd>
          </>
        )}
        {a.file && (
          <>
            <dt>{t("Arquivo")}</dt>
            <dd>
              {a.file.name} ({a.file.kind.toUpperCase()}, {fileSize(a.file.size_bytes)}) <span className="sub">SHA-256 {a.file.sha256.slice(0, 16)}…</span>
            </dd>
          </>
        )}
      </dl>

      <section className="panel section" aria-labelledby="comparacao">
        <div className="panel-head one-line">
          <h2 id="comparacao">{t("Prescrito × realizado")}</h2>
          {a.comparison && <Badge tone={TOTALS_STATUS[a.comparison.totals.status].tone}>{TOTALS_STATUS[a.comparison.totals.status].label}</Badge>}
        </div>
        <div className="panel-body stack">
          {a.comparison ? (
            <ComparisonView c={a.comparison} matchMethod={a.match_method} workoutHref={workoutHref} />
          ) : (
            <EmptyState title={t("Atividade sem treino vinculado")}>
              {a.candidates.length
                ? t("Escolha o treino publicado que corresponde a esta atividade.")
                : t("Não há treino publicado sem atividade entre 3 dias antes e 3 dias depois desta data.")}
            </EmptyState>
          )}
          {canEdit && !a.comparison && a.candidates.length > 0 && (
            <form
              className="inline-form"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                run("link", () => api.post<ActivityDetail>(`${path}/link`, { planned_workout_id: linkTo }), t("Atividade vinculada ao treino."));
              }}
            >
              <SelectField label={t("Treino")} value={linkTo} onChange={(e) => setLinkTo(e.target.value)}>
                {a.candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {fmtLong(c.date)}: {c.title}
                    {c.planned_distance_m ? ` (${metersToKm(c.planned_distance_m)})` : ""}
                  </option>
                ))}
              </SelectField>
              <Button type="submit" variant="primary" busy={busy === "link"} disabled={!linkTo}>
                {t("Vincular")}
              </Button>
            </form>
          )}
          {canEdit && a.comparison && (
            <div>
              <Button busy={busy === "unlink"} onClick={() => run("unlink", () => api.post<ActivityDetail>(`${path}/unlink`), t("Vínculo desfeito."))}>
                {t("Desfazer vínculo")}
              </Button>
            </div>
          )}
        </div>
      </section>

      {(a.sport === "run" || a.sport === "trail_run" || a.sport === "treadmill") && (
        <RpePanel a={a} path={path} canRate={canRate} onSaved={setA} />
      )}

      {a.metrics && <ActivityMetricsView m={a.metrics} />}

      {stream?.data.lat && stream.data.lon && (
        <section className="panel section" aria-labelledby="percurso">
          <div className="panel-head">
            <h2 id="percurso">{t("Percurso no mapa")}</h2>
          </div>
          <div className="panel-body">
            <RouteMap stream={stream} />
          </div>
        </section>
      )}

      {stream && (
        <section className="panel section" aria-labelledby="graficos">
          <div className="panel-head">
            <h2 id="graficos">{t("Ritmo, frequência cardíaca e altimetria")}</h2>
          </div>
          <div className="panel-body">
            <ActivityChart stream={stream} />
          </div>
        </section>
      )}

      {a.athlete_id && (self || canMessage) && (
        <section className="panel section" aria-labelledby="conversa-atividade">
          <div className="panel-head">
            <h2 id="conversa-atividade">{self ? t("Comentários do treinador") : t("Feedback e comentários")}</h2>
          </div>
          <div className="panel-body">
            <ConversationPanel
              key={a.id}
              orgId={orgId}
              athleteId={a.athlete_id}
              activityId={a.id}
              self={self}
              empty={self ? t("Nenhum comentário sobre esta atividade ainda. Você também pode escrever para a equipe aqui.") : t("Nenhum comentário ainda. Escreva um feedback sobre esta atividade: o atleta recebe um aviso.")}
            />
          </div>
        </section>
      )}

      {a.laps.length > 0 && (!a.comparison || !a.comparison.steps.aligned) && (
        <section className="section" aria-labelledby="voltas">
          <h2 id="voltas">{t("Voltas registradas")}</h2>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>{t("Volta")}</th>
                  <th className="right">{t("Distância")}</th>
                  <th className="right">{t("Tempo")}</th>
                  <th className="right">{t("Ritmo")}</th>
                  <th className="right">{t("FC média")}</th>
                </tr>
              </thead>
              <tbody>
                {a.laps.map((l) => (
                  <tr key={l.index}>
                    <td className="num">{l.index + 1}</td>
                    <td className="right num">{metersToKm(l.distance_m)}</td>
                    <td className="right num">{formatDuration(l.duration_s)}</td>
                    <td className="right num">{formatPace(l.pace_s_km)}</td>
                    <td className="right num">{l.avg_hr ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <EditActivityModal open={editing} a={a} path={path} onClose={() => setEditing(false)} onSaved={(x) => { setA(x); setEditing(false); toast(t("Atividade atualizada.")); }} />
      <Modal
        open={deleting}
        title={t("Excluir atividade")}
        onClose={() => setDeleting(false)}
        footer={
          <>
            <Button onClick={() => setDeleting(false)}>{t("Cancelar")}</Button>
            <Button
              variant="danger"
              busy={busy === "delete"}
              onClick={async () => {
                setBusy("delete");
                try {
                  await api.delete(path);
                  toast(t("Atividade excluída."));
                  navigate(back.to);
                } catch (e) {
                  toast((e as ApiError).message, "error");
                  setBusy(null);
                }
              }}
            >
              {t("Excluir atividade")}
            </Button>
          </>
        }
      >
        <p>
          {t("A atividade sai do histórico e o vínculo com o treino é desfeito. O arquivo original continua guardado; você pode importá-lo de novo depois.")}
        </p>
      </Modal>
    </>
  );
}

function ComparisonView({ c, matchMethod, workoutHref }: { c: Comparison; matchMethod: string | null; workoutHref?: (id: string) => string }) {
  const pw = c.planned_workout;
  const tp = c.totals;
  const s = c.steps;
  return (
    <>
      <p>
        {workoutHref ? <Link to={workoutHref(pw.id)}>{pw.title}</Link> : <strong>{pw.title}</strong>} — {fmtLong(pw.date)},{" "}
        {WORKOUT_TYPE_LABEL[pw.workout_type]}. {matchMethod === "auto" ? t("Vinculado automaticamente.") : t("Vinculado manualmente.")}
        {pw.summary_text && <span className="sub">{pw.summary_text}</span>}
      </p>
      <div className="table-wrap">
        <table className="data compare-totals">
          <thead>
            <tr>
              <th />
              <th className="right">{t("Planejado")}</th>
              <th className="right">{t("Realizado")}</th>
              <th className="right">{t("Diferença")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">{t("Distância")}</th>
              <td className="right num">{plannedDistance(tp.distance.planned_m, tp.distance.quality ?? "unknown") || "—"}</td>
              <td className="right num">{metersToKm(tp.distance.actual_m)}</td>
              <td className="right num">{pct(tp.distance.diff_pct)}</td>
            </tr>
            <tr>
              <th scope="row">{t("Duração")}</th>
              <td className="right num">{plannedDuration(tp.duration.planned_s, tp.duration.quality ?? "unknown") || "—"}</td>
              <td className="right num">{formatDuration(tp.duration.actual_s)}</td>
              <td className="right num">{pct(tp.duration.diff_pct)}</td>
            </tr>
            <tr>
              <th scope="row">{t("Esforço (1–10)")}</th>
              <td className="right num">{pw.expected_rpe ?? "—"}</td>
              <td className="right num">{pw.rpe ?? "—"}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      {tp.distance.quality === "partial" && (
        <p className="muted" style={{ fontSize: "var(--step--1)" }}>
          {t("A distância planejada é um mínimo (há trechos por tempo sem ritmo-alvo), por isso o realizado tende a ficar acima.")}
        </p>
      )}

      <h3 className="h-sub">{t("Trecho a trecho")}</h3>
      {!s.aligned ? (
        <p className="muted">{s.reason}</p>
      ) : (
        <>
          {s.active_with_target > 0 && (
            <p>
              {tj("{count} trechos de esforço dentro do ritmo-alvo (tolerância de 5 s/km).", {
                count: <strong className="num">{t("{a} de {b}", { a: s.active_on_target, b: s.active_with_target })}</strong>,
              })}
            </p>
          )}
          <div className="table-wrap">
            <table className="data responsive compare-steps">
              <thead>
                <tr>
                  <th>{t("Trecho")}</th>
                  <th>{t("Planejado")}</th>
                  <th className="right">{t("Distância")}</th>
                  <th className="right">{t("Tempo")}</th>
                  <th className="right">{t("Ritmo")}</th>
                  <th className="right">{t("FC")}</th>
                  <th>{t("Ritmo × alvo")}</th>
                </tr>
              </thead>
              <tbody>
                {s.rows.map((r) => (
                  <tr key={r.index} className={`phase-${r.phase}`}>
                    <td>{r.label}</td>
                    <td data-label={t("Planejado")} className="num">
                      {plannedVolume(r.planned.distance_m, r.planned.duration_s)}
                      {(r.planned.pace_min_s_km || r.planned.pace_max_s_km) && (
                        <span className="sub">{paceRange(r.planned.pace_min_s_km, r.planned.pace_max_s_km)}</span>
                      )}
                    </td>
                    <td data-label={t("Distância")} className="right num">
                      {metersToKm(r.actual.distance_m)}
                      {r.target === "distance" && r.volume_deviation_pct !== null && <span className="sub">{pct(r.volume_deviation_pct)}</span>}
                    </td>
                    <td data-label={t("Tempo")} className="right num">
                      {formatDuration(r.actual.duration_s)}
                      {r.target === "time" && r.volume_deviation_pct !== null && <span className="sub">{pct(r.volume_deviation_pct)}</span>}
                    </td>
                    <td data-label={t("Ritmo")} className="right num">{formatPace(r.actual.pace_s_km)}</td>
                    <td data-label={t("FC")} className="right num">{r.actual.avg_hr ?? "—"}</td>
                    <td data-label={t("Resultado")}>
                      <Badge tone={PACE_RESULT[r.pace_result].tone}>{PACE_RESULT[r.pace_result].label}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <p className="muted" style={{ fontSize: "var(--step--1)" }}>
        {" "}{t("Cálculo {algorithm_version}: volume cumprido entre 90% e 115% do planejado.", { algorithm_version: c.algorithm_version })}{" "}</p>
    </>
  );
}

function EditActivityModal({
  open,
  a,
  path,
  onClose,
  onSaved,
}: {
  open: boolean;
  a: ActivityDetail;
  path: string;
  onClose: () => void;
  onSaved: (a: ActivityDetail) => void;
}) {
  const [name, setName] = useState(a.name);
  const [sport, setSport] = useState(a.sport);
  const [notes, setNotes] = useState(a.notes ?? "");
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setName(a.name);
      setSport(a.sport);
      setNotes(a.notes ?? "");
      setError(null);
    }
  }, [open, a]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      onSaved(await api.patch<ActivityDetail>(path, { name, sport, notes }));
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  }
  const fe = error?.fieldErrors() ?? {};
  return (
    <Modal
      open={open}
      title={t("Editar atividade")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("Cancelar")}</Button>
          <Button variant="primary" type="submit" form="edit-activity" busy={busy}>
            {t("Salvar")}
          </Button>
        </>
      }
    >
      <form id="edit-activity" className="form" onSubmit={submit}>
        <FormError error={error && !Object.keys(fe).length ? error : null} />
        <TextField label={t("Nome")} value={name} onChange={(e) => setName(e.target.value)} error={fe.name} maxLength={160} required />
        <SelectField label={t("Modalidade")} value={sport} onChange={(e) => setSport(e.target.value as ActivityDetail["sport"])}>
          {Object.entries(SPORT_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </SelectField>
        <TextArea label={t("Observações")} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
      </form>
    </Modal>
  );
}
